import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';

export const appointSecretaryFlow: ChatFlow = {
  id: 'sg-appoint-secretary',
  taskCodeMatch: ['B32103'],
  categoryMatch: [
    'appoint secretary',
    'appointment of secretary',
    'appointment of company secretary',
    'secretary appointment',
    'new secretary',
  ],

  getInitialMessages(preSet): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME}**.\n\n` +
          (docCount > 0 ? `This preset includes ${docCount} document(s).\n\n` : '') +
          `First, enter a **Task Title**. Then choose the **Singapore company** to continue.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **Singapore company** for this appoint secretary task.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select entity (Appoint secretary).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. In **Stage 2 (Form 45B)** click ✨ on the name to pick the **secretary (contact)**, fill the dates, ' +
        'then click **Create Task**.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'nationality-b04',
      aiMessage: 'Select **nationality** from the list below.',
      widget: 'cm-base-code-list',
      widgetConfig: {
        targetVarKey: '_nationalityB04',
        targetEntityIndex: 0,
        baseCodeCountry: 'CM',
        baseCodeMain: 'B04',
        baseCodeListLabel: 'Nationality — please select',
      },
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a contact from the entity.',
      widget: 'officer-list',
      widgetConfig: {
        targetVarKey: '_officer',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person selected.',
      widget: null,
      nextStep: 'confirm',
    },
    {
      id: 'address-list',
      aiMessage: 'Pick an address for the secretary form.',
      widget: 'address-options',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Enter the address. It will be filled back to the form automatically.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [
          {
            role: 'user',
            content: String(title),
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${entityName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const fg = String(ctx.fieldGroup || '');

      if (fg === 'directorNationality' || fg === 'secretaryNationality') {
        return {
          messages: [
            {
              role: 'user',
              content: 'Opening **nationality** list.',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'nationality-b04',
          nextStepWidgetData: {
            baseCodeCountry: 'CM',
            baseCodeMain: 'B04',
            baseCodeListLabel: 'Nationality — please select',
          },
        };
      }

      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!entityCd) {
        return {
          messages: [
            {
              role: 'ai',
              content: 'Please select an entity first.',
              timestamp: Date.now(),
              stepId: 1,
              source: 'system',
            },
          ],
        };
      }

      if (ctx.defaultRole) {
        let lookupLine: string;
        if (fg === 'secretaryFirm') {
          lookupLine = `Looking up **Secretary firm** (role: ${ctx.defaultRole})...`;
        } else if (fg === 'appointedDirector') {
          lookupLine = `Looking up **Appointed director** (role: ${ctx.defaultRole})...`;
        } else if (fg === 'appointedSecretary') {
          lookupLine = `Looking up **Appointed secretary** (role: ${ctx.defaultRole})...`;
        } else {
          lookupLine = `Looking up **${fg || 'person'}** (role: ${ctx.defaultRole})...`;
        }
        return {
          messages: [
            {
              role: 'user',
              content: lookupLine,
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'officer-list',
          nextStepWidgetData: {
            entityCd,
            selectedRole: ctx.defaultRole,
          },
        };
      }

      return {
        messages: [
          {
            role: 'user',
            content: `Looking up **${ctx.fieldGroup || 'contact'}** (role: CONTACT)...`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          entityCd,
          selectedRole: 'CONTACT',
        },
      };
    }

    if (eventName === 'base-code-select' && context.currentStepId === 'nationality-b04') {
      const p = payload || {};
      const code = String(p.totalCode || '').trim();
      const name = String(p.codeName || p.title || '').trim() || code;
      return {
        messages: [
          {
            role: 'user',
            content: code ? `Selected nationality: **${name}** (${code})` : `Selected nationality: **${name}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected person: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-detail',
      };
    }

    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const fg = String(ctx.fieldGroup || '');
      if (fg !== 'secretaryAddress' && fg !== 'secretaryAlternateAddress') return {};

      const label = fg === 'secretaryAlternateAddress'
        ? 'Secretary alternate address'
        : 'Secretary address';

      if (ctx.directInput) {
        return {
          messages: [
            {
              role: 'user',
              content: `Enter **${label}** (Local Singapore or Foreign).`,
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'address-input',
        };
      }

      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      if (opts.length <= 1) {
        return {
          messages: [
            {
              role: 'user',
              content: `Enter **${label}** (Local Singapore or Foreign).`,
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'address-input',
        };
      }

      return {
        messages: [
          {
            role: 'user',
            content: `Choose an address for **${ctx.subjectLabel || label}**.`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts },
      };
    }

    if (eventName === 'entity-field-select' && context.currentStepId === 'address-input') {
      const value = typeof payload === 'string' ? payload : payload?.value || payload?.address || '';
      if (!value || !String(value).trim()) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Entered: **${String(value).trim()}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${opt.label || opt.type || 'Address'}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
